<!--
  DocumentStackPreview — layered "card stack" preview for a bulk selection.

  Shows the top VISIBLE_CARD_COUNT documents as real thumbnails, layered front-to-back
  with a small offset so the selection reads as a physical stack. Any further documents
  are hinted as imageless ghost cards behind them — so we never load more than
  VISIBLE_CARD_COUNT thumbnails regardless of how large the selection is.

  Layering model: layer 0 = front (top card, no offset, highest z-index). Higher layers
  sit further back (more offset, slightly smaller, lower z-index). Real cards occupy the
  front layers; ghost cards occupy the back layers.

  Props:
    documents — the currently selected Document objects, in list order
-->
<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { api, type Document } from '../api';
  import SignedThumbnailImage from './SignedThumbnailImage.svelte';

  export let documents: Document[] = [];

  /** Maximum number of real thumbnail images to load. */
  const VISIBLE_CARD_COUNT = 3;
  /** Maximum number of ghost (imageless) cards hinted behind the real ones. */
  const MAX_GHOST_CARDS = 2;

  // Per-layer offset in % of the card, so the stack stays visible at full page size (fixed pixels
  // vanished once the cards grew to the column width). Cards are 92 % × 94 % of the A4 box, which
  // leaves room for two back layers up and to the right inside the box.
  const OFFSET_X = 4; // %
  const OFFSET_Y = 3; // %

  // The front card is shown at page size: it uses the first page image (1600 px, Spec 117), which
  // stays sharp where the 1024 px thumbnail would be upscaled. Falls back to the large thumbnail.
  let frontPageUrl: string | null = null;
  let frontId: string | null = null;
  $: loadFrontPage(documents[0]?.id ?? null);

  async function loadFrontPage(id: string | null) {
    frontId = id;
    frontPageUrl = null;
    if (!id) return;
    try {
      const result = await api.documents.getPageImageUrls(id);
      if (frontId === id) frontPageUrl = result?.pages?.[0]?.url ?? null;
    } catch {
      // No page images (older document, unsupported type): the thumbnail stays.
    }
  }

  $: count = documents.length;
  $: visibleDocuments = documents.slice(0, VISIBLE_CARD_COUNT);
  $: ghostCount = Math.min(Math.max(count - visibleDocuments.length, 0), MAX_GHOST_CARDS);
  $: totalLayers = visibleDocuments.length + ghostCount;

  /** CSS for a card at the given stack layer (0 = front/top). */
  function cardStyle(layer: number): string {
    const tx = layer * OFFSET_X;
    const ty = -layer * OFFSET_Y;
    // Back cards are dimmed instead of shrunk: shrinking cancels most of the offset.
    const dim = 1 - layer * 0.15;
    const z = totalLayers - layer; // front layer → highest z-index
    return `transform: translate(${tx}%, ${ty}%); filter: brightness(${dim}); z-index: ${z};`;
  }
</script>

<!-- Same page surface as DocumentPreview: an A4 box that takes the height the host column gives it
     (width follows from the ratio, clamped to the column); stacked without a host height it is the
     full-width page. The two h-7 spacers stand in for DocumentPreview's open-original row and
     pager, so the page has the same size in single and multi selection; the offset back cards
     reach into them and into the pane padding. -->
<div class="flex flex-1 min-h-0 flex-col gap-2 select-none">
  <div class="h-7 shrink-0" aria-hidden="true"></div>
  <div class="flex flex-1 min-h-0 justify-center">
    <div
      class="relative h-full w-fit max-w-full"
      data-testid="stack-preview-page"
      aria-label={$_('pages.inbox.stack_preview_label', { values: { count } })}
    >
      <!-- Ghost cards (furthest back) -->
      {#each Array(ghostCount) as _, i (i)}
        <div
          class="absolute bottom-0 left-0 h-[94%] w-[92%] rounded-lg border border-border bg-bg-alt shadow-sm"
          style={cardStyle(visibleDocuments.length + i)}
          aria-hidden="true"
        ></div>
      {/each}

      <!-- Real thumbnail cards (layer 0 = front/top) -->
      {#each visibleDocuments as doc, i (doc.id)}
        <div
          class="absolute bottom-0 left-0 h-[94%] w-[92%] rounded-lg border border-border bg-surface shadow-sm overflow-hidden"
          style={cardStyle(i)}
          aria-hidden={i > 0}
        >
          {#if i === 0 && frontPageUrl}
            <img
              src={frontPageUrl}
              alt={doc.fileName}
              class="w-full h-full object-cover bg-white"
              on:error={() => (frontPageUrl = null)}
            />
          {:else}
            <SignedThumbnailImage
              documentId={doc.id}
              size={i === 0 ? 'large' : 'medium'}
              alt={doc.fileName}
              className="w-full h-full object-cover"
              lazy
            >
              <div
                slot="fallback"
                class="w-full h-full flex items-center justify-center bg-bg-alt p-2"
              >
                <span class="text-xs text-text-muted italic text-center break-words"
                  >{doc.fileName}</span
                >
              </div>
            </SignedThumbnailImage>
          {/if}
        </div>
      {/each}

      <!-- Spacer: an invisible A4-shaped svg gives the box its width and height like the page image
         of DocumentPreview (fills the column height, clamped to the column width; the large
         intrinsic size lets max-h/max-w scale it up to the host). -->
      <svg
        viewBox="0 0 210 297"
        width="2100"
        height="2970"
        class="block h-auto w-auto max-h-full max-w-full"
        aria-hidden="true"
      ></svg>
    </div>
  </div>
  <div class="h-7 shrink-0" aria-hidden="true"></div>
</div>
